import useCommonWidget, { UseCommonWidgetReturnInterface } from './useCommonWidget';
import { LocalStorageHistoriesNames } from '../enums/local-storage.enum';
import { HotelsDisneyService } from '../services/hotels-disney.service';
import { SearchWidgetInterface } from '../interfaces/search.interface';
import { AssistancesService } from '../services/assistances.service';
import { ActivitiesService } from '../services/activities.service';
import { EventsWindowCustom } from '../enums/events-window.enum';
import { PoseidonService } from '../services/poseidon.service';
import { FlightsService } from '../services/flights.service';
import { HotelsService } from '../services/hotels.service';
import { DisneyService } from '../services/disney.service';
import { PlansService } from '../services/plans.service';
import { useCallback, useEffect, useState } from 'react';
import { CarService } from '../services/cars.service';

type ServiceType =
	| CarService
	| FlightsService
	| ActivitiesService
	| HotelsService
	| AssistancesService
	| PlansService
	| DisneyService
	| HotelsDisneyService
	| PoseidonService;

export interface UseSearchWidgetInterface<T extends ServiceType, U> extends SearchWidgetInterface<U> {
	className?: string;
	styles?: string;
	productServiceClass: new (token: string) => T;
	customHistoryName?: LocalStorageHistoriesNames;
}

export interface UseSearchWidgetReturnInterface<T extends ServiceType, U> extends UseCommonWidgetReturnInterface<T> {
	urlSearchHandler: (data: U) => void;
	showMobileFieldsHandler: (show: boolean) => void;
}

const useSearchWidget = <T extends ServiceType, U>({
	token,
	language,
	agency,
	className,
	styles,
	callUrlSearch,
	callShowMobileFields,
	productServiceClass,
	customHistoryName,
}: UseSearchWidgetInterface<T, U>): UseSearchWidgetReturnInterface<T, U> => {
	const [tokenWidget, setTokenWidget] = useState<string | undefined>(token);
	useEffect(() => {
		const handleUpdateToken = (event: Event): void => {
			const customEvent = event as CustomEvent;
			const { token }: { token: string } = customEvent.detail;
			setTokenWidget(token);
		};

		window.addEventListener(EventsWindowCustom.updateToken, handleUpdateToken);

		return () => {
			window.removeEventListener(EventsWindowCustom.updateToken, handleUpdateToken);
		};
	}, []);

	const { agencyState, isLoading, agencyError, widgetService, widgetStyles, t } = useCommonWidget({
		token: tokenWidget,
		language,
		agency,
		className,
		styles,
		WidgetServiceClass: productServiceClass,
		customHistoryName,
	});

	const urlSearchHandler = useCallback(
		(data: U) => {
			if (callUrlSearch != null) {
				callUrlSearch(data);
			}
		},
		[callUrlSearch],
	);

	const showMobileFieldsHandler = useCallback(
		(show: boolean): void => {
			if (callShowMobileFields != null) {
				callShowMobileFields(show);
			}
		},
		[callShowMobileFields],
	);

	return {
		agencyState,
		isLoading,
		agencyError,
		widgetService,
		widgetStyles,
		urlSearchHandler,
		showMobileFieldsHandler,
		t,
	};
};

export default useSearchWidget;
